import {
    animate,
    AnimationEvent,
    state,
    style,
    transition,
    trigger,
} from "@angular/animations";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";

import { toggleContentAnimation } from "@hermes/aphrodite/animations";
import { LayoutFacade } from "@hermes/aphrodite/layout";
import { AnalyticsService } from "@hermes/utils/analytics";
import {
    DEFAULT_FILTER_BAR_HEIGHT,
    GridFacade,
    GridFilterGroup,
    isSwitchFilterActive,
} from "@hermes/states/grid-filters-state";

import { GridFilterToggleEvent } from "../../events/grid-filter-toggle.event";

/**
 * This component implements a collapsible element (Toggle).
 *
 */
@Component({
    selector: "h-grid-toggle",
    templateUrl: "./grid-toggle.component.html",
    styleUrls: ["./grid-toggle.component.scss"],
    animations: [
        toggleContentAnimation,
        trigger("togglerIcon", [
            state(
                "hidden",
                style({
                    transform: "rotate(0)",
                }),
            ),
            state(
                "visible",
                style({
                    transform: "rotate(180deg)",
                }),
            ),
            transition("hidden <=> visible", animate("200ms ease-in-out")),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridToggleComponent implements OnInit, OnDestroy, OnChanges {
    /**
     * Content container
     */
    @ViewChild("toggleContent")
    public contentElement: ElementRef;

    @ViewChild("filterButton")
    public filterButtonEl: ElementRef;

    /**
     * Collapsed flag, can be used with double-data binding to control the toggle.
     */
    @Input()
    public collapsed: boolean = true;

    @Input()
    public filterGroupName: string;

    @Input()
    public gridFilterGroup: GridFilterGroup;

    /**
     * Notify parent of collapsed value change.
     */
    @Output()
    public collapsedChange: EventEmitter<string> = new EventEmitter<string>();

    public countSelectedFilters: number;
    public isSwitchChecked$: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);
    private subscription: Subscription = new Subscription();

    constructor(
        private gridFacade: GridFacade,
        private renderer: Renderer2,
        private cd: ChangeDetectorRef,
        private analyticsService: AnalyticsService,
        public layoutFacade: LayoutFacade,
    ) {}

    @HostListener("window:scroll")
    public closeIfOpenedOnScroll(): void {
        if (!this.collapsed) {
            this.collapsedChange.emit(undefined);
        }
    }

    public ngOnInit(): void {
        if (this.gridFilterGroup.presentation === "switch") {
            // Initialize switch
            this.isSwitchChecked$.next(
                isSwitchFilterActive(this.gridFilterGroup),
            );

            this.subscription.add(
                this.gridFacade
                    .selectSwitchFilterActive$(this.filterGroupName)
                    .pipe(distinctUntilChanged())
                    .subscribe((switchState) => {
                        this.isSwitchChecked$.next(switchState);
                        this.cd.markForCheck();
                    }),
            );
        } else {
            this.subscription.add(
                this.gridFacade
                    .selectCountSelectedFilter$(this.filterGroupName)
                    .pipe(distinctUntilChanged())
                    .subscribe((count) => {
                        this.countSelectedFilters = count;
                        this.cd.markForCheck();
                    }),
            );
        }

        this.subscription.add(
            this.layoutFacade.headerMainHeight$
                .pipe(distinctUntilChanged())
                .subscribe((headerHeight: number) => {
                    if (headerHeight && this.contentElement) {
                        if (headerHeight > DEFAULT_FILTER_BAR_HEIGHT) {
                            this.renderer.setStyle(
                                this.contentElement.nativeElement,
                                "top",
                                `${headerHeight}px`,
                            );
                        } else {
                            this.renderer.removeStyle(
                                this.contentElement.nativeElement,
                                "top",
                            );
                        }
                    }
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.collapsed &&
            changes.collapsed.previousValue !== undefined &&
            changes.collapsed.currentValue !== changes.collapsed.previousValue
        ) {
            this.analyticsService.sendData(
                new GridFilterToggleEvent({
                    closed: this.collapsed,
                    filterName: this.filterGroupName,
                }),
            );
        }
    }

    /**
     * Show or hide the toggle
     */
    public handleHeaderToggle(): void {
        this.collapsedChange.emit(
            this.collapsed ? this.filterGroupName : undefined,
        );
    }

    public toggleClassAfterAnimation(event: AnimationEvent): void {
        if (
            (event.fromState === "visible" || event.fromState === "void") &&
            event.toState === "hidden"
        ) {
            this.contentElement.nativeElement.classList.add("hidden");
            return;
        }
        this.contentElement.nativeElement.classList.remove("hidden");
    }

    public switchFilterClicked(event: boolean): void {
        const filter = this.gridFilterGroup.filters.find(
            (f) => f.name === (event ? "On" : "Off"),
        );
        if (!filter) {
            return;
        }
        filter.selected = !filter.selected;

        this.gridFacade.updateGridFilter(filter, this.gridFilterGroup);
    }

    /**
     * Display switch when presentation is 'switch' and there is a different number of product on each switch options
     */
    public shouldDisplaySwitch(): boolean {
        return (
            this.gridFilterGroup.presentation === "switch" &&
            this.gridFilterGroup.filters.length === 2 &&
            this.gridFilterGroup.filters[0].total !==
                this.gridFilterGroup.filters[1].total
        );
    }
}
